import { type ReactNode } from "react";
import { IconChevronRight, IconExternalLink } from "@tabler/icons-react";
import { cn } from "../../../lib/utils";
import { Switch } from "../../ui/Switch";
import { Avatar } from "../../ui/Avatar";

// The trailing affordance of a settings row. A discriminated union so callers
// pick exactly one shape and the renderer stays exhaustive. The `switch` variant
// carries its own handler because it owns its interaction (the DS `Switch`
// component is the control) rather than delegating to the row's tap.
export type SettingsRowRight =
  | { kind: "chevron" }
  | { kind: "value"; text: string }
  | { kind: "text"; text: string }
  | { kind: "external" }
  | {
      kind: "switch";
      checked: boolean;
      onCheckedChange: (checked: boolean) => void;
    };

type SettingsRowProps = {
  /** 20px icon rendered inside the rounded-square avatar, or null for a plain
   *  list row (no avatar) as used by the Account / Notifications / Language
   *  sub-lists. */
  icon: ReactNode | null;
  title: string;
  subtitle: string | null;
  right: SettingsRowRight;
  /** Row tap handler for navigational rows; null for `switch` rows, where the
   *  `Switch` component owns the interaction. */
  onClick: (() => void) | null;
  tone: "default" | "danger";
};

function RowRight({ right }: { right: SettingsRowRight }) {
  switch (right.kind) {
    case "chevron":
      return (
        <IconChevronRight
          size={20}
          stroke={1.5}
          className="shrink-0 text-ds-slate-400"
        />
      );
    case "external":
      return (
        <IconExternalLink
          size={20}
          stroke={1.5}
          className="shrink-0 text-ds-slate-400"
        />
      );
    case "value":
      return (
        <span className="flex min-w-0 shrink items-center gap-1">
          <span className="truncate text-ui-note text-ds-slate-500">
            {right.text}
          </span>
          <IconChevronRight
            size={20}
            stroke={1.5}
            className="shrink-0 text-ds-slate-400"
          />
        </span>
      );
    case "text":
      return (
        <span className="min-w-0 shrink truncate text-ui-note text-ds-slate-500">
          {right.text}
        </span>
      );
    case "switch":
      return (
        <Switch
          checked={right.checked}
          onCheckedChange={right.onCheckedChange}
        />
      );
    default:
      throw new Error(right satisfies never);
  }
}

export function SettingsRow({
  icon,
  title,
  subtitle,
  right,
  onClick,
  tone,
}: SettingsRowProps): React.JSX.Element {
  const content = (
    <>
      {icon !== null ? (
        <Avatar
          variant="icon"
          size="md"
          icon={icon}
          badge={null}
          className={
            tone === "danger"
              ? "bg-ds-clementine-100 border-ds-clementine-100 text-ds-clementine-600"
              : ""
          }
        />
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col gap-0">
        <span
          className={cn(
            "text-ui-body font-semibold",
            tone === "danger" ? "text-ds-clementine-600" : "text-ds-black"
          )}
        >
          {title}
        </span>
        {subtitle !== null ? (
          <span className="text-footnote text-ds-slate-700">{subtitle}</span>
        ) : null}
      </span>
      <RowRight right={right} />
    </>
  );

  // Non-interactive rows (switch rows — where the DS `Switch` is the control —
  // and static rows with no tap handler) render as a plain div so we never nest
  // a button or expose a dead button. Everything else is a real button.
  if (right.kind === "switch" || onClick === null) {
    return (
      <div className="flex w-full items-center gap-3 px-1 py-2 text-left">
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-1 py-2 text-left"
    >
      {content}
    </button>
  );
}
